import Keycloak from 'keycloak-js';
import type { KeycloakInitOptions, KeycloakProfile } from 'keycloak-js';
import { getKeycloakConfig } from '../../config/keycloak.config';

/**
 * AuthService - manages Keycloak authentication
 * Based on Angular AuthService from formize-ui project
 */
class AuthService {
  private keycloak: Keycloak;
  private _isInitialized = false;
  private _isAuthenticated = false;
  private _profile?: KeycloakProfile;
  private _userInfo?: any;
  
  public logoutRedirectUrl?: string;

  constructor() {
    const config = getKeycloakConfig();
    console.log('[AuthService] ==========================================');
    console.log('[AuthService] Keycloak configuration:');
    console.log('[AuthService] URL:', config.url);
    console.log('[AuthService] Realm:', config.realm);
    console.log('[AuthService] Client ID:', config.clientId);
    console.log('[AuthService] ==========================================');
    this.keycloak = new Keycloak(config);
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  get userName(): string {
    return this._userInfo?.name ?? '';
  }

  get email(): string {
    return this._userInfo?.email ?? '';
  }

  get userId(): string {
    return this._userInfo?.sub ?? '';
  }

  /**
   * Initialize Keycloak client
   * @param onLoad - Specifies an action to do on load ('check-sso' | 'login-required')
   */
  async init(onLoad?: 'login-required' | 'check-sso'): Promise<void> {
    if (this._isInitialized) {
      console.log('[AuthService] Already initialized');
      return;
    }

    try {
      // Using Authorization Code Flow with PKCE (modern standard for SPA)
      const initOptions: KeycloakInitOptions = {
        onLoad: onLoad ?? 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        flow: 'standard', // Authorization Code Flow
        pkceMethod: 'S256', // Enable PKCE for security
        checkLoginIframe: false, // Disabled to prevent redirect loops
        enableLogging: true, // Enable debug logs
      };

      console.log('[AuthService] Initializing Keycloak with options:', initOptions);
      console.log('[AuthService] Current URL:', window.location.href);
      
      this._isAuthenticated = await this.keycloak.init(initOptions);
      
      console.log('[AuthService] Init completed. Authenticated:', this._isAuthenticated);

      // Setup auto-refresh token
      this.keycloak.onTokenExpired = async () => {
        console.log('[AuthService] Token expired, refreshing...');
        try {
          const refreshed = await this.keycloak.updateToken(30);
          if (refreshed) {
            console.log('[AuthService] Token refreshed successfully');
          }
        } catch (error) {
          console.error('[AuthService] Failed to refresh token:', error);
          // Token refresh failed - user needs to re-login
          await this.logout();
        }
      };

      this._isInitialized = true;
      console.log(`[AuthService] User is ${this._isAuthenticated ? 'authenticated' : 'not authenticated'}`);
      
      if (this._isAuthenticated) {
        await this.updateProfile();
      }
    } catch (error) {
      console.error('[AuthService] Failed to initialize Keycloak:', error);
      this._isInitialized = true;
      this._isAuthenticated = false;
    }
  }

  /**
   * Redirect to Keycloak login page
   * @param locale - Language code for login page
   */
  async login(locale?: string): Promise<void> {
    if (this._isAuthenticated) {
      console.log('[AuthService] Already authenticated');
      return;
    }
    
    console.log('[AuthService] Redirecting to login...');
    // Don't specify redirectUri - let Keycloak determine it automatically (like Formize)
    await this.keycloak.login({
      locale,
      // NO redirectUri - Keycloak will use current URL
    });
  }

  /**
   * Logout user and redirect
   */
  async logout(): Promise<void> {
    if (!this._isAuthenticated) {
      console.log('[AuthService] Not authenticated, nothing to logout');
      return;
    }
    
    console.log('[AuthService] Logging out...');
    // Only use logoutRedirectUrl if explicitly set (like Formize)
    await this.keycloak.logout({
      redirectUri: this.logoutRedirectUrl
    });
  }

  /**
   * Redirect to Keycloak registration page
   * @param locale - Language code for registration page
   */
  async register(locale?: string): Promise<void> {
    if (this._isAuthenticated) {
      console.log('[AuthService] Already authenticated');
      return;
    }
    
    console.log('[AuthService] Redirecting to registration...');
    // Don't specify redirectUri - let Keycloak determine it automatically (like Formize)
    await this.keycloak.register({
      locale,
      // NO redirectUri - Keycloak will use current URL
    });
  }

  /**
   * Get current access token (auto-refreshes if needed)
   * @returns Access token or undefined if not authenticated
   */
  async getToken(): Promise<string | undefined> {
    if (!this.isAuthenticated) {
      return undefined;
    }
    
    try {
      // Update token if it expires in less than 5 seconds
      await this.keycloak.updateToken(5);
      return this.keycloak.token;
    } catch (error) {
      console.error('[AuthService] Failed to get token:', error);
      return undefined;
    }
  }

  /**
   * Load user profile and info from Keycloak
   */
  private async updateProfile(): Promise<void> {
    if (this._isAuthenticated) {
      try {
        this._profile = await this.keycloak.loadUserProfile();
        this._userInfo = await this.keycloak.loadUserInfo();
        console.log('[AuthService] User profile loaded:', {
          name: this.userName,
          email: this.email,
          userId: this.userId
        });
      } catch (error) {
        console.error('[AuthService] Failed to load user profile:', error);
      }
    } else {
      this._profile = undefined;
      this._userInfo = undefined;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

